import { useState } from "react";
import { FormItemTypes, FormPublishStatus } from "../../../utils/formBuilderUtils";
import { generateID } from "../../../utils/common";
import { useAppDispatch } from "../../../redux/hooks";
import { openModal } from "../../../redux/uireducers/modalstrip";
import moment from "moment";
import { saveTemplate } from "../../../redux/entities/formBuilderEntity";
import useModalStrip from "../../../global-hooks/useModalStrip";

const useFormBuilder = (props) => {
  const [selectedTemplate, setSelectedTemplate] = useState(props.template || null);
  const [formLayoutComponents, setFormLayoutComponents] = useState(
    props.template?.formLayoutComponents || []
  );
  const [selectedControl, setSelectedControl] = useState(undefined);

  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();

  // Handles a Container or a Component added to the form builder
  const handleItemAdded = (item, containerId) => {
    if (item.itemType === FormItemTypes.CONTAINER) {
      const newState = [...formLayoutComponents];
      newState.push({
        container: { ...item, id: generateID() },
        children: [],
      });
      setFormLayoutComponents(newState);
    } else if (item.itemType === FormItemTypes.CONTROL) {
      const newState = [...formLayoutComponents];
      const formContainerId = newState.findIndex((f) => f.container.id === containerId);
      const formContainer = { ...newState[formContainerId] };
      const obj = { ...item, id: generateID(), containerId };

      // Deep copy items
      if (item.items) {
        obj.items = JSON.parse(JSON.stringify(item.items));
      }
      
      formContainer.children = [...formContainer.children, obj];
      newState[formContainerId] = formContainer;
      setFormLayoutComponents(newState);
    }
  };

  // Delete a container from the layout
  const deleteContainer = (containerId) => {
    if (window.confirm("Are you sure you want to delete the container?")) {
      const newState = formLayoutComponents.filter((comp) => comp.container.id !== containerId);
      setFormLayoutComponents(newState);
      setSelectedControl((prev) =>
        prev && (prev.id === containerId || prev.containerId === containerId) ? undefined : prev
      );
    }
  };

  // Delete a control from the layout
  const deleteControl = (controlId, containerId) => {
    const newState = [...formLayoutComponents];
    const formContainer = newState.find((comp) => comp.container.id === containerId);
    if (formContainer) {
      formContainer.children = formContainer.children.filter((cont) => cont.id !== controlId);
    }
    setFormLayoutComponents(newState);
    setSelectedControl((prev) => (prev && prev.id === controlId ? undefined : prev));
  };

  // Select a control on click
  const selectControl = (item) => {
    setSelectedControl(item);
  };

  // Edit properties of the control from the Sidebar
  const editControlProperties = (item) => {
    // Create a deep copy of the state to avoid mutating the original state
    const newState = formLayoutComponents.map((comp) => ({
      ...comp,
      children: [...comp.children],
    }));
  
    // Find the container by ID
    const formContainerId = newState.findIndex((comp) => comp.container.id === item.containerId);
  
    if (formContainerId !== -1) {
      const formContainer = newState[formContainerId];
  
      // Find the control by its ID
      const controlIndex = formContainer.children.findIndex((cont) => cont.id === item.id);
  
      if (controlIndex !== -1) {
        // Replace the control in the children array
        formContainer.children[controlIndex] = { ...item };
  
        // Update the state
        setFormLayoutComponents(newState);
      }
    }
  };
  

  // Edit properties of the container
  const editContainerProperties = (item) => {
    const newState = [...formLayoutComponents];
    const formContainerId = newState.findIndex((comp) => comp.container.id === item.id);
    if (formContainerId !== -1) {
      const formContainer = { ...newState[formContainerId] };
      formContainer.container = {
        ...formContainer.container,
        heading: item.heading,
        subHeading: item.subHeading,
      };
      newState[formContainerId] = formContainer;
      setFormLayoutComponents(newState);
    }
  };

  // Move a control from the sidebar based on dropdown values
  const moveControlFromSide = (item, { containerId, position }) => {
    let componentsCopy = [...formLayoutComponents];

    const currentItemContainer = componentsCopy.find((con) => con.container.id === item.containerId);
    const moveItemToContainer = componentsCopy.find((con) => con.container.id === containerId);

    if (currentItemContainer && moveItemToContainer) {
      const itemIndex = currentItemContainer.children.findIndex((con) => con.id === item.id);
      if (itemIndex !== -1) {
        const deletedItem = currentItemContainer.children.splice(itemIndex, 1)[0];
        deletedItem.containerId = containerId;

        if (position !== undefined) {
          moveItemToContainer.children.splice(position, 0, deletedItem);
        } else {
          moveItemToContainer.children.push(deletedItem);
        }
        setFormLayoutComponents(componentsCopy);
        setSelectedControl(deletedItem);
      }
    }
  };

  // Move control when dragged to a different position
  const moveControl = (item, dragIndex, hoverIndex, containerId) => {
    if (!item) return;
  
    let componentsCopy = [...formLayoutComponents];
  
    const formContainer = componentsCopy.find((con) => con.container.id === containerId);
  
    if (formContainer) {
      // Ensure indices are valid before splicing
      if (dragIndex >= 0 && dragIndex < formContainer.children.length) {
        const deletedItem = formContainer.children.splice(dragIndex, 1)[0];
  
        if (deletedItem && hoverIndex >= 0 && hoverIndex <= formContainer.children.length) {
          formContainer.children.splice(hoverIndex, 0, deletedItem);
        }
      }
  
      // Update state after performing modifications
      setFormLayoutComponents(componentsCopy);
    }
  };
  

  const checkIfControlsInContainer = () => {
    for (let i = 0; i < formLayoutComponents.length; i++) {
      if (formLayoutComponents[i].children.length === 0) {
        showModalStrip(
          "danger",
          "You need to have controls inside containers before updating.",
          5000
        );
        return false;
      }
    }
    return true;
  };

  const publishForm = () => {
    if (formLayoutComponents.length === 0) {
      showModalStrip("danger", "Form cannot be empty", 5000);
      return;
    }
  
    if (!checkIfControlsInContainer()) return;
  
    // Create a deep copy of selectedTemplate to avoid mutation of frozen objects
    let currentTemplate = { ...selectedTemplate, publishHistory: [...selectedTemplate.publishHistory] };
  
    // Check if there are changes in the form layout components
    if (
      currentTemplate.publishHistory.length > 0 &&
      JSON.stringify(currentTemplate.publishHistory[0].formLayoutComponents) ===
        JSON.stringify(formLayoutComponents)
    ) {
      showModalStrip("info", "No Change in current & previous published version.", 5000);
      return;
    }
  
    const updatedAt = moment().unix() * 1000;
  
    // Add previous publish history if applicable
    if (currentTemplate.lastPublishedAt !== 0) {
      currentTemplate.publishHistory.unshift({
        lastPublishedAt: currentTemplate.lastPublishedAt,
        formLayoutComponents: currentTemplate.formLayoutComponents,
      });
    }
  
    // Update current template details
    currentTemplate.formLayoutComponents = formLayoutComponents;
    currentTemplate.publishStatus = FormPublishStatus.PUBLISHED;
    currentTemplate.lastPublishedAt = updatedAt;
    currentTemplate.updatedAt = updatedAt;
  
    // Dispatch the saveTemplate action
    dispatch(saveTemplate(currentTemplate))
      .unwrap()
      .then((newTemplate) => {
        setSelectedTemplate(newTemplate);
        showModalStrip("success", "Changes in Form Published.", 5000);
      });
  };
  
  

  const saveForm = () => {
    if (formLayoutComponents.length === 0) {
      showModalStrip("danger", "Form cannot be empty", 5000);
      return;
    }

    if (!checkIfControlsInContainer()) return;

    const currentTemplate = { ...selectedTemplate };

    if (
      JSON.stringify(currentTemplate.formLayoutComponents) ===
      JSON.stringify(formLayoutComponents)
    ) {
      showModalStrip("info", "No Change in current & previous saved version.", 5000);
      return;
    }

    currentTemplate.formLayoutComponents = formLayoutComponents;
    currentTemplate.publishStatus = FormPublishStatus.DRAFT;
    currentTemplate.updatedAt = moment().unix() * 1000;

    dispatch(saveTemplate(currentTemplate)).unwrap().then(() => {
      showModalStrip("success", "Changes in Form Saved.", 5000);
    });
  };

  return {
    handleItemAdded,
    deleteContainer,
    deleteControl,
    selectControl,
    editContainerProperties,
    editControlProperties,
    moveControlFromSide,
    moveControl,
    publishForm,
    saveForm,
    selectedTemplate,
    formLayoutComponents,
    selectedControl,
  };
};

export default useFormBuilder;
